/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  Checkbox,
  Badge
} from '@mui/material';
import { CalendarToday, Search } from '@mui/icons-material';

interface PeriodoItem {
  CodPeriodo: string;
  DesPeriodo: string;
  [key: string]: any;
}

// Props para modo individual (default)
interface SingleModeProps {
  multiple?: false;
  selectedDate: string;
  onDateChange: (codPeriodo: string, desPeriodo: string) => void;
  selectedDates?: never;
  onDatesChange?: never;
}

// Props para modo múltiple
interface MultipleModeProps {
  multiple: true;
  selectedDates: string[];
  onDatesChange: (selectedCodes: string[], selectedItems: PeriodoItem[]) => void;
  selectedDate?: never;
  onDateChange?: never;
}

type DateSelectorButtonProps = {
  dates: PeriodoItem[];
  groupBy?: string;
  buttonVariant?: 'text' | 'outlined' | 'contained';
  buttonColor?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  modalTitle?: string;
  acceptButtonText?: string;
  cancelButtonText?: string;
  placeholder?: string;
  modalMinWidth?: string | number;
  modalHeight?: string | number;
} & (SingleModeProps | MultipleModeProps);

interface GroupedItems {
  [groupName: string]: PeriodoItem[];
}

const DateSelectorButton: React.FC<DateSelectorButtonProps> = ({
  dates,
  groupBy,
  buttonVariant = 'outlined',
  buttonColor = 'primary',
  modalTitle = 'Seleccionar fecha',
  acceptButtonText = 'Aceptar',
  cancelButtonText = 'Cancelar',
  placeholder = 'Seleccionar fecha',
  modalMinWidth = '280px',
  modalHeight = '500px',
  multiple = false,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [tempSelectedDates, setTempSelectedDates] = useState<string[]>(() => {
    if (multiple) {
      return props.selectedDates || [];
    } else {
      return props.selectedDate ? [props.selectedDate] : [];
    }
  });
  const [expandedGroup, setExpandedGroup] = useState<string | false>(false);
  const [orderedGroupKeys, setOrderedGroupKeys] = useState<string[]>([]);
  const [groupedData, setGroupedData] = useState<GroupedItems | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (multiple) {
      setTempSelectedDates(props.selectedDates || []);
    } else {
      setTempSelectedDates(props.selectedDate ? [props.selectedDate] : []);
    }
  }, [multiple, props.selectedDates, props.selectedDate]);

  const getSelectedDescription = () => {
    if (multiple) {
      const count = tempSelectedDates.length;
      if (count === 0) return placeholder;
      if (count === 1) {
        const selected = dates.find(d => d.CodPeriodo === tempSelectedDates[0]);
        return selected ? selected.DesPeriodo : placeholder;
      }
      return `${count} fechas seleccionadas`;
    } else {
      const selected = dates.find(d => d.CodPeriodo === tempSelectedDates[0]);
      return selected ? selected.DesPeriodo : placeholder;
    }
  };

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return dates;
    
    const term = searchTerm.toLowerCase();
    return dates.filter(item => 
      item.CodPeriodo.toLowerCase().includes(term) || 
      item.DesPeriodo.toLowerCase().includes(term)
    );
  }, [dates, searchTerm]);

  const groupsWithMatches = useMemo(() => {
    if (!groupBy || !searchTerm.trim() || !groupedData) return new Set<string>();
    
    const term = searchTerm.toLowerCase();
    const matchingGroups = new Set<string>();
    
    Object.entries(groupedData).forEach(([groupName, items]) => {
      const hasMatch = items.some(item => 
        item.CodPeriodo.toLowerCase().includes(term) || 
        item.DesPeriodo.toLowerCase().includes(term)
      );
      if (hasMatch) {
        matchingGroups.add(groupName);
      }
    });
    
    return matchingGroups;
  }, [groupBy, searchTerm, groupedData]);

  const groupItems = () => {
    if (!groupBy || !filteredItems.length) {
      setOrderedGroupKeys([]);
      setGroupedData(null);
      return;
    }

    const grouped: GroupedItems = {};
    const keys: string[] = [];
    
    filteredItems.forEach(item => {
      const groupValue = item[groupBy];
      if (!groupValue) return;
      
      const groupKey = String(groupValue);
      if (!grouped[groupKey]) {
        keys.push(groupKey);
        grouped[groupKey] = [];
      }
      grouped[groupKey].push(item);
    });
    setOrderedGroupKeys(keys);
    setGroupedData(grouped);
  };

  const findGroupWithSelectedItem = (): string | null => {
    if (!groupBy || tempSelectedDates.length === 0) return null;
    if (!groupedData) return null;

    for (const [groupName, items] of Object.entries(groupedData)) {
      if (items.some((item: PeriodoItem) => tempSelectedDates.includes(item.CodPeriodo))) {
        return groupName;
      }
    }
    return null;
  };

  useEffect(() => {
    if (open && groupBy && groupedData && searchTerm.trim()) {
      const groupsToExpand = groupsWithMatches;
      if (groupsToExpand.size > 0) {
        const firstGroup = Array.from(groupsToExpand)[0];
        setExpandedGroup(firstGroup);
      } else if (expandedGroup !== false) {
        setExpandedGroup(false);
      }
    }
  }, [searchTerm, groupedData, open, groupBy]);

  useEffect(() => {
    if (open && groupBy && tempSelectedDates.length > 0 && !searchTerm.trim()) {
      const groupWithSelected = findGroupWithSelectedItem();
      if (groupWithSelected) {
        setExpandedGroup(groupWithSelected);
      }
    }
  }, [tempSelectedDates, open, groupBy, searchTerm]);

  useEffect(() => {
    if (!groupBy) {
      setOrderedGroupKeys([]);
      setGroupedData(null);
      return;
    }
    groupItems();
  }, [filteredItems, groupBy]);

  const handleOpen = () => {
    if (multiple) {
      setTempSelectedDates(props.selectedDates || []);
    } else {
      setTempSelectedDates(props.selectedDate ? [props.selectedDate] : []);
    }
    setSearchTerm('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleAccept = () => {
    if (multiple) {
      const selectedItems = dates.filter(d => tempSelectedDates.includes(d.CodPeriodo));
      props.onDatesChange?.(tempSelectedDates, selectedItems);
    } else {
      if (tempSelectedDates.length > 0) {
        const selectedCode = tempSelectedDates[0];
        const selectedItem = dates.find(d => d.CodPeriodo === selectedCode);
        if (selectedItem && props.onDateChange) {
          props.onDateChange(selectedItem.CodPeriodo, selectedItem.DesPeriodo);
        }
      }
    }
    setOpen(false);
  };

  const handleSelectDate = (codPeriodo: string) => {
    if (multiple) {
      setTempSelectedDates(prev => {
        if (prev.includes(codPeriodo)) {
          return prev.filter(c => c !== codPeriodo);
        } else {
          return [...prev, codPeriodo];
        }
      });
    } else {
      setTempSelectedDates([codPeriodo]);
    }
  };

  const handleAccordionChange = (groupName: string) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedGroup(isExpanded ? groupName : false);
  };

  const isSelected = (codPeriodo: string) => tempSelectedDates.includes(codPeriodo);

  const renderListItem = (item: PeriodoItem) => (
    <ListItem
      key={item.CodPeriodo}
      onClick={() => handleSelectDate(item.CodPeriodo)}
      sx={{
        cursor: 'pointer',
        borderLeft: !multiple && isSelected(item.CodPeriodo) 
          ? '3px solid' 
          : '3px solid transparent',
        borderLeftColor: !multiple && isSelected(item.CodPeriodo) 
          ? 'primary.main' 
          : 'transparent',
        backgroundColor: !multiple && isSelected(item.CodPeriodo) 
          ? 'action.selected' 
          : 'transparent',
        '&:hover': {
          backgroundColor: 'action.hover'
        },
        transition: 'all 0.2s',
        py: 0.75,
        minHeight: '32px'
      }}
    >
      {multiple && (
        <Checkbox
          checked={isSelected(item.CodPeriodo)}
          size="small"
          sx={{ mr: 1, py: 0 }}
        />
      )}
      <ListItemText 
        primary={item.DesPeriodo}
        primaryTypographyProps={{
          variant: 'body2',
          fontWeight: isSelected(item.CodPeriodo) ? 500 : 400,
          fontSize: '0.875rem'
        }}
      />
    </ListItem>
  );

  return (
    <>
      <FormControl size="small">
        <InputLabel shrink id="scales-label" style={{color: 'white'}}>
          Hasta
        </InputLabel>
        <Badge
          badgeContent={multiple ? tempSelectedDates.length : 0}
          color="primary"
          invisible={!multiple || tempSelectedDates.length === 0}
          sx={{ '& .MuiBadge-badge': { right: -5, top: 5 } }}
        >
          <Button
            variant={buttonVariant}
            onClick={handleOpen}
            startIcon={<CalendarToday />}
            sx={{
              minWidth: '10em',
              justifyContent: 'space-between',
              textTransform: 'none',
              '& .MuiButton-startIcon': {
                mr: 1
              },
              borderColor: 'rgba(0, 0, 0, 0.30)',
              color: 'black',
              '&:focus': {
                outline: 'none',
              },
              '&:focus-visible': {
                outline: 'none',
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>{getSelectedDescription()}</span>
            </Box>
          </Button>
        </Badge>
      </FormControl>

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            height: modalHeight,
            maxHeight: modalHeight,
            minWidth: modalMinWidth,
            width: 'auto',
            m: 2,
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          {modalTitle}
        </DialogTitle>
        
        <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Buscar por código o descripción..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2, flexShrink: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        
          <Box sx={{ overflow: 'auto', flex: 1 }}>
            {orderedGroupKeys.length > 0 && groupedData ? (
              <Box sx={{ width: '100%' }}>
                {orderedGroupKeys.map((groupName) => {
                  const items = groupedData[groupName];
                  if (items.length === 0) return null;
                  
                  const shouldExpand = expandedGroup === groupName;
                  
                  return (
                    <Accordion
                      key={groupName}
                      expanded={shouldExpand}
                      onChange={handleAccordionChange(groupName)}
                      sx={{
                        boxShadow: 'none',
                        '&:before': {
                          display: 'none'
                        },
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        margin: 0,
                        '&.Mui-expanded': {
                          margin: 0
                        }
                      }}
                    >
                      <AccordionSummary 
                        sx={{ 
                          backgroundColor: 'action.hover',
                          minHeight: '36px',
                          '& .MuiAccordionSummary-content': {
                            margin: '6px 0'
                          }
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight="medium">
                          {groupName}
                        </Typography>
                        <Chip 
                          label={items.length} 
                          size="small" 
                          sx={{ ml: 1, height: '20px', '& .MuiChip-label': { fontSize: '0.7rem', px: 1 } }}
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <List disablePadding>
                          {items.map((item) => renderListItem(item))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
                {orderedGroupKeys.length === 0 && searchTerm && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No se encontraron resultados
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {filteredItems.map((item) => renderListItem(item))}
                {filteredItems.length === 0 && searchTerm && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No se encontraron resultados
                    </Typography>
                  </Box>
                )}
              </List>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Button onClick={handleClose} color="inherit">
            {cancelButtonText}
          </Button>
          <Button 
            onClick={handleAccept} 
            color="primary" 
            variant="contained"
            disabled={!multiple && tempSelectedDates.length === 0}
          >
            {acceptButtonText}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DateSelectorButton;